/**
 * Where the shared Company / Brand / Market header filters survive a reload.
 *
 * The trio is chosen once and read on eight list screens (Offers, Product
 * Family, Purchase Orders, Product Variation, Inventory, Mail Plan, Stock,
 * Bundles). Holding it in React state alone carries it across client-side
 * navigation and loses it on F5, which is the half of the complaint a context
 * provider cannot fix on its own. This is the other half.
 *
 * ── Why sessionStorage ──────────────────────────────────────────────────────
 * `localStorage` would restore a filter a week later on a machine the user
 * shares, and a list that opens pre-filtered with no memory of why is a support
 * ticket, not a feature. It is also browser-wide, so two tabs working on two
 * companies would overwrite each other.
 *
 * The URL is the shareable option and it is the better answer eventually, but
 * it needs every one of the eight routes plus every internal `router.push`
 * (row links, detail screens, the sidebar) to carry the three params or the
 * choice is dropped the moment the user clicks a row. That is a bigger change
 * than this one and belongs in its own pass.
 *
 * `sessionStorage` is per tab and dies with it: filters live exactly as long as
 * the work does, two tabs keep two answers, and closing the tab is a full
 * cleanup that needs no code.
 *
 * ── Multi-tenant ────────────────────────────────────────────────────────────
 * These are org-scoped uuids, and sign-out does NOT clear sessionStorage: the
 * departure is `window.location.assign` in the same tab (`useSignOut`), so the
 * store outlives the principal. The record therefore carries the organization
 * AND the user it was written for, and `readFilterScope` refuses to hand back a
 * record written by anyone else, deleting it on the way out. Nothing about
 * another tenant is ever read back, and nothing about them is left behind
 * either.
 */

/** One chosen row: `id` is what the fetchers take, `name` is what pages that filter by name take. */
export interface StoredFacet {
  /** Row id. Empty string means "all", the spelling every fetcher and query key already uses. */
  id: string;
  /** Display name of the chosen row, empty when nothing is chosen. */
  name: string;
}

/** The three shared filters as they are held in memory and on disk. */
export interface StoredFilterScope {
  company: StoredFacet;
  brand: StoredFacet;
  market: StoredFacet;
}

/** Who a stored record belongs to. A record matching neither field is not ours to read. */
export interface FilterScopePrincipal {
  organizationId: string;
  userId: string;
}

/**
 * Bumped whenever the record shape changes. An older key is simply never read,
 * so a shape change degrades to "filters start empty once" rather than to a
 * parse branch nobody maintains.
 */
const STORAGE_KEY = 'westmont.filter-scope.v1';

/** Nothing chosen. Module-level so React can bail out of a same-value update. */
export const EMPTY_FACET: StoredFacet = Object.freeze({ id: '', name: '' });

/** All three filters at "all". */
export const EMPTY_FILTER_SCOPE: StoredFilterScope = Object.freeze({
  company: EMPTY_FACET,
  brand: EMPTY_FACET,
  market: EMPTY_FACET,
});

/** True when at least one of the three is set. */
export function hasSelection(scope: StoredFilterScope): boolean {
  return Boolean(scope.company.id || scope.brand.id || scope.market.id);
}

/** How many of the three are set. Drives the count on "Clear all filters". */
export function countSelection(scope: StoredFilterScope): number {
  return [scope.company.id, scope.brand.id, scope.market.id].filter(Boolean).length;
}

/**
 * `sessionStorage`, or null where it cannot be reached.
 *
 * Two real cases, neither of them an error worth surfacing: the static export's
 * pre-render pass has no `window`, and Safari in private mode throws on the
 * property itself rather than on the call. A browser without a store just means
 * filters do not survive F5.
 */
function store(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isFacet(value: unknown): value is StoredFacet {
  if (typeof value !== 'object' || value === null) return false;
  const facet = value as Record<string, unknown>;
  return typeof facet.id === 'string' && typeof facet.name === 'string';
}

/**
 * Reads the record back, or null when there is nothing to restore.
 *
 * Returns null and DELETES the record when it was written by another
 * organization or another user, which is the sign-out case: the next person to
 * sign in on this tab starts clean instead of inheriting a company id from
 * someone else's tenant. A malformed record is treated the same way, since the
 * only thing to do with one is drop it.
 */
export function readFilterScope(principal: FilterScopePrincipal): StoredFilterScope | null {
  const storage = store();
  if (!storage) return null;

  let raw: string | null = null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const sameOrg = parsed.organizationId === principal.organizationId;
    const sameUser = parsed.userId === principal.userId;
    if (
      sameOrg &&
      sameUser &&
      isFacet(parsed.company) &&
      isFacet(parsed.brand) &&
      isFacet(parsed.market)
    ) {
      return { company: parsed.company, brand: parsed.brand, market: parsed.market };
    }
  } catch {
    // Falls through to the wipe below: unparseable is as unusable as foreign.
  }

  clearFilterScope();
  return null;
}

/**
 * Stores the trio, stamped with the principal it belongs to.
 *
 * An all-empty selection removes the record rather than writing three empty
 * strings, so "Clear all filters" leaves nothing behind to restore and no
 * tenant id sitting in the store for a screen that is not filtered anyway.
 */
export function writeFilterScope(principal: FilterScopePrincipal, scope: StoredFilterScope): void {
  const storage = store();
  if (!storage) return;

  if (!hasSelection(scope)) {
    clearFilterScope();
    return;
  }

  try {
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        organizationId: principal.organizationId,
        userId: principal.userId,
        company: scope.company,
        brand: scope.brand,
        market: scope.market,
      }),
    );
  } catch {
    // Quota or a blocked store. The filters still work for this page view.
  }
}

/** Drops the record. Safe to call when there is none. */
export function clearFilterScope(): void {
  const storage = store();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do, and nothing the user could act on.
  }
}
